import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import OfferCard from '@/components/OfferCard';
import { Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { fetchFeaturedOffers } from '@/lib/api/offers';
import type { EnhancedOffer } from '@/lib/api/offers/types';

export default function FeaturedOffers() {
  const [offers, setOffers] = useState<EnhancedOffer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    
    async function loadOffers() {
      try {
        setLoading(true);
        setError(null);
        
        const featuredOffers = await fetchFeaturedOffers();
        
        if (isMounted) {
          setOffers(featuredOffers);
          setLoading(false);
        }
      } catch (err) {
        console.error('Error in FeaturedOffers component:', err);
        if (isMounted) {
          setError('Failed to load featured offers');
          setLoading(false);
        }
      }
    }

    loadOffers();

    return () => {
      isMounted = false;
    };
  }, []);

  if (loading) {
    return (
      <div className="py-8 md:py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="p-8 flex justify-center">
            <Loader2 className="h-10 w-10 animate-spin" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-8 md:py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="p-8 text-center">
            <p className="text-red-500">{error}</p>
            <Button 
              variant="outline" 
              className="mt-4" 
              onClick={() => {
                setLoading(true);
                fetchFeaturedOffers()
                  .then(data => {
                    setOffers(data);
                    setLoading(false);
                    setError(null);
                  })
                  .catch(() => {
                    setError('Failed to reload featured offers');
                    setLoading(false);
                  });
              }}
            >
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (offers.length === 0) {
    return (
      <div className="py-8 md:py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="p-8 text-center">
            <p className="text-muted-foreground">No featured offers available at the moment.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <section className="py-8 md:py-16 bg-white">
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
          <h2 className="text-2xl font-bold">Featured Offers</h2>
          <Link to="/marketplace">
            <Button variant="outline" className="w-full sm:w-auto flex items-center gap-2">
              View All
            </Button>
          </Link>
        </div>
        
        <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {offers.map((offer) => (
            <OfferCard 
              key={offer.id}
              id={offer.id}
              title={offer.title}
              price={offer.price_cents / 100}
              dr={offer.dr}
              da={offer.da}
              traffic={offer.traffic_estimate}
              linkType={offer.link_type}
              turnaroundDays={offer.turnaround_days}
              tags={offer.tags}
              sellerName={offer.seller_profile.name}
              sellerId={offer.seller_profile_id || ""}
              sellerRating={4.5}
              sellerReviewCount={10}
              slug={offer.slug}
              sellerSlug={offer.seller_profile.slug}
              sellerVerified={offer.seller_profile.verified}
              sellerAvatar=""
            />
          ))}
        </div>
      </div>
    </section>
  );
}
